import React, {FC, useCallback, useMemo, useRef, useState} from 'react';
import {StyleSheet, View, LayoutChangeEvent} from 'react-native';
import Carousel, {
  ICarouselInstance,
  Pagination,
} from 'react-native-reanimated-carousel';
import Styles from '../../Styles';
import withIsConnected from '../../hoc/withIsConnected';
import MoblicoPromotion from './MoblicoPromotion.web';
import type {MoblicoPromotionType} from 'src/types/Promotions';
import {useSharedValue} from 'react-native-reanimated';

type MoblicoPromotionsCarouselProps = {
  promos: Array<MoblicoPromotionType>;
  isConnected: boolean;
  promotionViewed: (promotion: any) => void;
};
const MoblicoPromotionsCarousel: FC<MoblicoPromotionsCarouselProps> = ({
  promos,
  isConnected,
  promotionViewed,
}) => {
  const carouselRef = useRef<ICarouselInstance>(null);
  const progress = useSharedValue<number>(0);
  const [width, setWidth] = useState(0);

  const onLayout = useCallback((event: LayoutChangeEvent) => {
    const {width: layoutWidth} = event.nativeEvent.layout;
    setWidth(layoutWidth);
  }, []);

  const onPressPagination = useCallback(
    (index: number) => {
      carouselRef.current?.scrollTo({
        count: index - progress.value,
        animated: true,
      });
    },
    [progress.value],
  );

  const renderItem = useCallback(
    ({item, index}) => (
      <MoblicoPromotion
        promotion={item}
        isConnected={isConnected}
        index={index}
        promotionViewed={promotionViewed}
      />
    ),
    [isConnected, promotionViewed],
  );

  const baseOptions = useMemo(() => {
    return {
      vertical: false,
      width,
      height: width * 0.8,
      enabled: promos.length > 1,
      mode: 'parallax',
      modeConfig: {
        parallaxScrollingOffset: 50,
        parallaxScrollingScale: 0.9,
      },
    } as const;
  }, [promos.length, width]);

  if (width <= 0) {
    return <View onLayout={onLayout} />;
  }

  return (
    <View onLayout={onLayout} style={styles.container}>
      <Carousel
        ref={carouselRef}
        {...baseOptions}
        onProgressChange={progress}
        style={[
          {
            width,
          },
          styles.carouselContainer,
        ]}
        data={promos}
        renderItem={renderItem}
        loop={false}
      />

      {promos?.length > 1 && (
        <Pagination.Basic
          progress={progress}
          data={promos}
          size={7}
          dotStyle={styles.paginationDot}
          activeDotStyle={styles.activePaginationDot}
          containerStyle={styles.paginationContainer}
          onPress={onPressPagination}
        />
      )}
    </View>
  );
};

const styles = StyleSheet.create({
  container: {
    alignItems: 'center',
    flex: 1,
    marginVertical: Styles.Spacing.m3,
    marginHorizontal: Styles.Spacing.m3,
  },
  carouselContainer: {
    marginBottom: Styles.Spacing.m3,
    flexGrow: 0,
  },
  paginationDot: {
    borderRadius: 70,
    backgroundColor: Styles.tabBarBackgroundColor,
  },
  activePaginationDot: {
    backgroundColor: Styles.footerTabActive,
  },
  paginationContainer: {
    gap: Styles.Spacing.m3,
    marginBottom: Styles.Spacing.m2,
  },
});

export default withIsConnected(MoblicoPromotionsCarousel);
